import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import {
  useCreateCredential,
  useCredentials,
  useProviderSettings,
  useRotateCredential,
  useProbeProvider,
  useUpdateCredential,
  useUpsertProviderSetting,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import { pracasApi } from '@/services/config';
import type { Credential, ProbeResult, ProviderSetting } from '@/types/config';

import styles from './ConfigExternalApis.module.css';

interface ConfigField {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'password' | 'select';
  /** Só para `select`. Escolha fechada existe porque "revizar" digitado à mão
   *  desliga a esteira em silêncio. */
  options?: { value: string; label: string }[];
  /** Opções que vêm do banco, não da lista fixa: hoje as praças (conexões ION HUB). */
  optionsFrom?: 'pracas';
  hint?: string;
  placeholder?: string;
}

interface ExternalApiSpec {
  providerKey: string;
  label: string;
  description: string;
  secretLabel: string;
  configFields: ConfigField[];
  /**
   * Onda 4 (config por tenant): a integração é DO CLIENTE, não da casa. A
   * config vai para `api_credential.config_json` (que já tem dono e é o que o
   * resolvedor do backend lê) em vez de `provider_setting`, que é uma linha
   * global e só staff pode escrever. Sem isto, o admin do tenant tomava 404 ao
   * salvar — e a `base_url` que ele digitasse nunca chegaria ao adapter.
   */
  tenantScoped?: boolean;
  /**
   * UMA CREDENCIAL POR PRAÇA. O slug da credencial passa a ser o slug da praça
   * escolhida no campo `praca` — o ARION de Belém não abre o de Manaus, e cada
   * praça tem endereço e token próprios. Sem isto a tela gravaria tudo em
   * `slug: 'main'` e a segunda praça sobrescreveria a primeira.
   */
  porPraca?: boolean;
}

// A API devolve valor sensível mascarado (`***` + 4 últimos) e reconhece a
// máscara de volta no PUT sem sobrescrever o segredo gravado.
const PREFIXO_MASCARA = '***';

// Exportado para o teste varrer a regra dos campos sensíveis (F-77).
export const EXTERNAL_APIS: ExternalApiSpec[] = [
  {
    providerKey: 'loterias_caixa',
    label: 'Loterias CAIXA',
    description:
      'Resultados oficiais de sorteios da Mega-Sena, Lotofácil, Quina. API direta + Scraper de fallback.',
    secretLabel: 'Chave / Token Privado (Opcional)',
    configFields: [
      { key: 'games', label: 'Jogos Ativos', placeholder: 'megasena, lotofacil, quina' },
      { key: 'frequency_seconds', label: 'Cadência de Checagem (seg)', type: 'number', placeholder: '300' },
      { key: 'fallback_enabled', label: 'Fallback Scraping (true/false)', placeholder: 'true' },
    ],
  },
  {
    providerKey: 'github_trending',
    label: 'GitHub Trending & Releases',
    description:
      'Monitoramento de lançamentos e releases em repositórios técnicos (vLLM, Ollama, LangChain).',
    secretLabel: 'GitHub Access Token (PAT)',
    configFields: [
      { key: 'repositories', label: 'Repositórios Monitorados', placeholder: 'ollama/ollama, vllm-project/vllm' },
      { key: 'frequency_seconds', label: 'Cadência de Checagem (seg)', type: 'number', placeholder: '600' },
    ],
  },
  {
    providerKey: 'serp',
    label: 'SERP API',
    description:
      'Google Trends, People-Also-Ask, busca orgânica. Usado em Trending Topics e SEO panel.',
    secretLabel: 'API key',
    configFields: [
      {
        key: 'provider_kind',
        label: 'Provider escolhido',
        type: 'text',
        hint: 'serpapi | searchapi  (define qual conector responde)',
        placeholder: 'serpapi',
      },
      { key: 'default_country', label: 'Default country', placeholder: 'br' },
      { key: 'default_language', label: 'Default language', placeholder: 'pt' },
      {
        key: 'default_location',
        label: 'Default location',
        placeholder: 'Brazil',
      },
      {
        key: 'cache_ttl_minutes',
        label: 'Cache TTL (min)',
        type: 'number',
        placeholder: '30',
      },
    ],
  },
  {
    providerKey: 'smtp',
    label: 'SMTP (notificações email)',
    description:
      'Servidor SMTP para o canal "email" das notificações. Recomendado: Amazon SES, SendGrid, Postmark. ' +
      '(o que você grava aqui ainda não é lido por ninguém: o envio de e-mail só usa as variáveis ' +
      'de ambiente SMTP_* do servidor — configurar por esta tela não dispara nada hoje)',
    secretLabel: 'Password',
    configFields: [
      {
        key: 'host',
        label: 'Host',
        placeholder: 'email-smtp.us-east-1.amazonaws.com',
      },
      { key: 'port', label: 'Port', type: 'number', placeholder: '587' },
      { key: 'user', label: 'User', placeholder: 'AKIA...' },
      {
        key: 'from_address',
        label: 'Remetente',
        placeholder: 'noreply@seudominio.com.br',
      },
      {
        key: 'use_tls',
        label: 'TLS (true/false)',
        type: 'text',
        placeholder: 'true',
      },
    ],
  },
  {
    providerKey: 'twitter',
    label: 'Twitter / X',
    // A descrição antiga ("Publicação automática de tweets a partir de stories")
    // era do fluxo do N8N, que saiu do produto. Nenhum código lê estas chaves:
    // core/providers.py:332 declara o provider e para aí; a publicação real no X
    // lê social_account.secret_enc (social_engine/engine.py:83), nunca o
    // provider_setting. E nada em services/ ou no scheduler publica em rede a
    // partir de story. O texto agora diz o que o card é de verdade.
    description:
      'Chaves legadas da API do X (v2). A publicação social HOJE é pelo motor de Redes Sociais (Contas) — este card fica para integrações antigas.',
    secretLabel: 'Bearer token',
    configFields: [
      // OAuth1 do X: os quatro valem tanto quanto o bearer. Sem type
      // 'password' o navegador os imprime em claro na tela (F-77).
      { key: 'api_key', label: 'API key', type: 'password' },
      { key: 'api_secret', label: 'API secret', type: 'password' },
      { key: 'access_token', label: 'Access token', type: 'password' },
      {
        key: 'access_token_secret',
        label: 'Access token secret',
        type: 'password',
      },
      { key: 'username', label: 'Username', placeholder: '@gncnews' },
    ],
  },
  {
    providerKey: 'freeastrology',
    label: 'FreeAstrology API',
    description:
      'API de horóscopos diários. Usado pelo Horóscopo Worker. Migrar do nó hardcoded no N8N.',
    secretLabel: 'API key',
    configFields: [
      { key: 'default_lang', label: 'Default language', placeholder: 'pt' },
      {
        key: 'timezone',
        label: 'Timezone',
        placeholder: 'America/Sao_Paulo',
      },
    ],
  },
  {
    providerKey: 'psi',
    label: 'PageSpeed Insights (Core Web Vitals)',
    description:
      'Mede LCP/CLS/INP das matérias (aba Otimização → "Medir CWV"). Chave grátis do Google Cloud, 25k/dia. Sem chave dá 429.',
    secretLabel: 'API key',
    configFields: [],
  },
  {
    providerKey: 'ion_hub',
    label: 'ION HUB (MOS / TV)',
    tenantScoped: true,
    description:
      'Recebe roteiros de TV (laudas MOS) via ION HUB e cria pautas pra adaptar TV→web. O worker puxa as laudas READY_TO_AIR a cada 60s.',
    secretLabel: 'Token (opcional)',
    configFields: [
      {
        key: 'base_url',
        label: 'URL do ION HUB',
        placeholder: 'http://ion-hub:8080',
        hint: 'Onde a API/dashboard do ION HUB responde (porta 8080).',
      },
    ],
  },
  {
    providerKey: 'arion',
    label: 'ARION (NRCS da praça)',
    tenantScoped: true,
    porPraca: true,
    description:
      'O ION HUB manda a lauda; o ARION diz o que ela É — formato da peça (VT, nota, link, merchandising, estúdio), se é notícia e quem é o repórter. É o que o gate do telejornal usa para barrar anúncio e segmento de estúdio, e para creditar o repórter na matéria. Uma credencial por praça.',
    secretLabel: 'Token do ARION',
    configFields: [
      {
        key: 'praca',
        label: 'Praça',
        type: 'select',
        optionsFrom: 'pracas',
        hint: 'A praça (conexão ION HUB) cujo ARION é este. Trocar a praça abre a credencial dela.',
      },
      {
        key: 'base_url',
        label: 'URL do ARION',
        placeholder: 'http://200.178.115.50:80',
        hint: 'Endereço e porta do NRCS da praça.',
      },
    ],
  },
  {
    providerKey: 'render',
    label: 'Cortes de vídeo (Klap)',
    tenantScoped: true,
    description:
      'Verticaliza o VT e recorta o Shorts. Em "Automático" a esteira gera sozinha assim que o VT chega. Cada versão gerada é cobrada pelo fornecedor.',
    secretLabel: 'Chave do fornecedor',
    configFields: [
      {
        key: 'motor',
        label: 'Fornecedor',
        type: 'select',
        options: [
          { value: 'klap', label: 'Klap' },
          { value: 'externo_json', label: 'Genérico (API JSON)' },
        ],
        hint: 'No Klap quem escolhe o trecho do corte é a IA dele; no genérico, o corte vai decidido daqui.',
      },
      {
        key: 'modo',
        label: 'Modo da praça',
        type: 'select',
        options: [
          { value: 'auto', label: 'Automático — gera e publica sozinho' },
          { value: 'revisar', label: 'Revisar — gera e espera alguém olhar' },
          { value: 'desligado', label: 'Desligado' },
        ],
      },
      {
        key: 'base_url',
        label: 'URL da API',
        placeholder: 'https://api.klap.app/v2',
        hint: 'Vazio usa a base do fornecedor escolhido.',
      },
      {
        key: 'style_preset_id',
        label: 'Preset de estilo (Klap)',
        hint: 'Criado no editor do Klap; copie o id no cartão do preset.',
      },
      {
        key: 'candidatos',
        label: 'Cortes candidatos por VT',
        type: 'number',
        placeholder: '1',
        hint: 'Cada candidato gerado é cobrado. 1 = o fornecedor manda o melhor.',
      },
      { key: 'idioma', label: 'Idioma do VT', placeholder: 'pt', hint: 'Vazio = o fornecedor detecta.' },
    ],
  },
  {
    providerKey: 'youtube',
    label: 'YouTube (canal do cliente)',
    tenantScoped: true,
    description:
      'Sobe a íntegra 16:9 e o Shorts ao canal. A chave de API confere o canal e o vídeo; PUBLICAR exige o OAuth do dono do canal (refresh token + client ID + client secret).',
    secretLabel: 'Refresh token do canal (OAuth)',
    configFields: [
      {
        key: 'api_key',
        label: 'Chave de API (YouTube Data)',
        type: 'password',
        hint: 'AIza… — confere canal e vídeo. Não sobe vídeo.',
      },
      { key: 'client_id', label: 'OAuth client ID' },
      { key: 'client_secret', label: 'OAuth client secret', type: 'password' },
      {
        key: 'channel_id',
        label: 'ID do canal',
        placeholder: 'UC…',
        hint: 'Com ele o teste devolve o nome do canal — a prova de que a chave enxerga o canal certo.',
      },
      {
        key: 'privacidade',
        label: 'Privacidade ao subir',
        type: 'select',
        options: [
          { value: 'unlisted', label: 'Não listado (padrão seguro)' },
          { value: 'public', label: 'Público' },
          { value: 'private', label: 'Privado' },
        ],
      },
      { key: 'categoria', label: 'Categoria do YouTube', placeholder: '25', hint: '25 = Notícias e Política.' },
    ],
  },
];

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

function descreveSondagem(r: ProbeResult): string {
  const partes = [r.ok ? 'O fornecedor respondeu.' : 'O fornecedor não aceitou.'];
  if (r.modes.length) partes.push(`Faz: ${r.modes.join(', ')}.`);
  if (r.models.length)
    partes.push(`Entrega: ${r.models.map((m) => m.label).join(', ')}.`);
  if (r.source === 'static' && r.ok)
    partes.push('(não deu para provar ao vivo — este fornecedor não tem rota de teste)');
  if (r.error) partes.push(r.error);
  return partes.join(' ');
}

export function ConfigExternalApis() {
  const { data: settings = [] } = useProviderSettings();
  const { data: credentials = [] } = useCredentials();
  const createCred = useCreateCredential();
  const updateCred = useUpdateCredential();
  const rotateCred = useRotateCredential();
  const upsertSetting = useUpsertProviderSetting();
  const probe = useProbeProvider();

  const settingByProvider = useMemo(
    () =>
      Object.fromEntries(
        settings.map((s: ProviderSetting) => [s.provider, s]),
      ),
    [settings],
  );
  const credByProvider = useMemo(
    () =>
      Object.fromEntries(
        credentials.map((c: Credential) => [c.provider, c]),
      ),
    [credentials],
  );
  // Cartão por praça guarda UMA credencial por slug de praça — o índice do banco
  // é (provider, slug, cliente). Sem esta chave composta a tela mostraria sempre
  // a primeira credencial do provedor, qualquer que fosse a praça escolhida.
  const credByChave = useMemo(
    () =>
      Object.fromEntries(
        credentials.map((c: Credential) => [`${c.provider}|${c.slug}`, c]),
      ),
    [credentials],
  );
  // As praças alimentam o campo "Praça" do cartão do ARION.
  const { data: pracas = [] } = useQuery({
    queryKey: ['config', 'pracas'],
    queryFn: pracasApi.list,
    staleTime: 5 * 60_000,
  });
  const opcoesDinamicas = useMemo(
    () => ({
      pracas: pracas.map((p) => ({
        value: p.slug,
        label: p.ativa ? p.nome : `${p.nome} (inativa)`,
      })),
    }),
    [pracas],
  );

  const [editing, setEditing] = useState<ExternalApiSpec | null>(null);
  const [secret, setSecret] = useState('');
  const [configForm, setConfigForm] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  // A esteira só usa credencial ATIVA. Sem isto na tela, uma chave certa e
  // inativa parecia "configurada" e nada acontecia — sem nada dizendo por quê.
  const [ativa, setAtiva] = useState(true);

  /** A credencial que este cartão edita agora: por praça, é a do slug da praça. */
  function credDo(spec: ExternalApiSpec | null, praca?: string): Credential | undefined {
    if (!spec) return undefined;
    if (!spec.porPraca) return credByProvider[spec.providerKey];
    return praca ? credByChave[`${spec.providerKey}|${praca}`] : undefined;
  }

  function openEdit(spec: ExternalApiSpec) {
    const cfg = (spec.tenantScoped
      ? (credDo(spec)?.config ?? {})
      : (settingByProvider[spec.providerKey]?.config ?? {})) as Record<
      string,
      unknown
    >;
    const draft: Record<string, string> = {};
    for (const field of spec.configFields) {
      const value = cfg[field.key];
      draft[field.key] = value === undefined || value === null ? '' : String(value);
    }
    setEditing(spec);
    setConfigForm(draft);
    setSecret('');
    setAtiva(credDo(spec)?.active ?? true);
    setMsg(null);
  }

  /** Trocar a praça abre a credencial DELA — endereço, estado e token próprios. */
  function trocarPraca(spec: ExternalApiSpec, praca: string) {
    const cred = credDo(spec, praca);
    const cfg = (cred?.config ?? {}) as Record<string, unknown>;
    const draft: Record<string, string> = { praca };
    for (const field of spec.configFields) {
      if (field.key === 'praca') continue;
      const value = cfg[field.key];
      draft[field.key] = value === undefined || value === null ? '' : String(value);
    }
    setConfigForm(draft);
    setSecret('');
    setAtiva(cred?.active ?? true);
    setMsg(null);
  }

  async function testar() {
    if (!editing) return;
    setMsg(null);
    // Valor mascarado nunca volta ao backend: ele o usaria como se fosse o
    // segredo, e o teste falharia por culpa da própria tela.
    const limpa: Record<string, unknown> = {};
    for (const [chave, valor] of Object.entries(configForm)) {
      const t = valor?.trim();
      if (t && !t.startsWith(PREFIXO_MASCARA)) limpa[chave] = t;
    }
    try {
      const r = await probe.mutateAsync({
        provider: editing.providerKey,
        payload: { api_key: secret || null, config: limpa, save: false },
      });
      setMsg(descreveSondagem(r));
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  function closeEdit() {
    setEditing(null);
    setConfigForm({});
    setSecret('');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setMsg(null);

    // Coerce numeric fields to numbers where it makes sense
    const campos: Record<string, unknown> = {};
    for (const field of editing.configFields) {
      const raw = configForm[field.key]?.trim();
      if (!raw) continue;
      if (field.type === 'number') {
        const n = Number(raw);
        if (Number.isFinite(n)) campos[field.key] = n;
      } else {
        campos[field.key] = raw;
      }
    }
    // O formulário é dono só do que ele mostra. Chave de config que este cartão
    // não declara (o perfil da praça, o alvo em ms, o ajuste do pool de SERP)
    // sobrevive ao "Guardar" — senão salvar a URL apagaria o crédito do rodapé
    // do vídeo, sem erro e sem aviso. Vale para os DOIS lados: a integração do
    // tenant guarda a config na credencial, a da casa no provider_setting.
    const praca = editing.porPraca ? (configForm.praca ?? '').trim() : '';
    if (editing.porPraca && !praca) {
      setMsg('Escolha a praça: a credencial é gravada por praça.');
      return;
    }
    const slugDaCredencial = praca || 'main';
    const guardado = (editing.tenantScoped
      ? credDo(editing, praca)?.config
      : settingByProvider[editing.providerKey]?.config) as
      | Record<string, unknown>
      | undefined;
    const naoDeclaradas: Record<string, unknown> = {};
    const declaradas = new Set(editing.configFields.map((f) => f.key));
    for (const [chave, valor] of Object.entries(guardado ?? {})) {
      if (!declaradas.has(chave)) naoDeclaradas[chave] = valor;
    }
    const configPayload = { ...naoDeclaradas, ...campos };

    try {
      const existingCred = credDo(editing, praca);

      if (!editing.tenantScoped) {
        await upsertSetting.mutateAsync({
          provider: editing.providerKey,
          config: configPayload,
        });
      } else if (existingCred) {
        // Integração do tenant: a config mora na própria credencial dele.
        await updateCred.mutateAsync({
          id: existingCred.id,
          payload: { config: configPayload, active: ativa },
        });
      }

      // Ligar/desligar vale para qualquer integração, não só as do tenant.
      if (existingCred && !editing.tenantScoped && ativa !== existingCred.active) {
        await updateCred.mutateAsync({
          id: existingCred.id,
          payload: { active: ativa },
        });
      }

      if (secret) {
        if (existingCred) {
          await rotateCred.mutateAsync({
            id: existingCred.id,
            payload: { secret, reason: 'updated via external-apis page' },
          });
        } else {
          await createCred.mutateAsync({
            provider: editing.providerKey,
            slug: slugDaCredencial,
            secret,
            config: configPayload,
            notes: `created via external-apis page`,
          });
        }
      } else if (editing.tenantScoped && !existingCred) {
        // Sem token (o ION HUB de referência não exige auth) a linha ainda
        // precisa existir: é ela que carrega a base_url E o dono.
        await createCred.mutateAsync({
          provider: editing.providerKey,
          slug: slugDaCredencial,
          config: configPayload,
          notes: `created via external-apis page`,
        });
      }

      setMsg(`${editing.label} atualizado.`);
      closeEdit();
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <h1 className={styles.title}>APIs externas</h1>
        <p className={styles.subtitle}>
          SERP, SMTP, Twitter/X, FreeAstrology e outros serviços de terceiros.
          As chaves ficam Fernet-encrypted. As configs da casa vão para
          <code> provider_setting</code>; as da integração do seu cliente (ION
          HUB) ficam na própria credencial, que já tem dono.
        </p>
      </header>

      {msg && <p className={styles.flash}>{msg}</p>}

      <ul className={styles.list}>
        {EXTERNAL_APIS.map((spec) => {
          const cred = credByProvider[spec.providerKey] as Credential | undefined;
          const settingRow = settingByProvider[spec.providerKey] as
            | ProviderSetting
            | undefined;
          // Integração do tenant: a config exibida é a da credencial dele.
          const cfg = (spec.tenantScoped ? cred?.config : settingRow?.config) as
            | Record<string, unknown>
            | undefined;
          const setting = spec.tenantScoped
            ? cfg && Object.keys(cfg).length > 0
              ? { config: cfg }
              : undefined
            : settingRow;
          return (
            <li key={spec.providerKey} className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{spec.label}</span>
                {cred?.has_secret ? (
                  <Badge tone="ok" size="sm">
                    chave configurada
                  </Badge>
                ) : (
                  <Badge tone="warn" size="sm">
                    sem chave
                  </Badge>
                )}
                {cred && !cred.active && (
                  <Badge tone="crit" size="sm">
                    inativa
                  </Badge>
                )}
                {setting && (
                  <Badge tone="info" size="sm">
                    config: {Object.keys(setting.config).length} keys
                  </Badge>
                )}
              </div>
              <p className={styles.desc}>{spec.description}</p>

              {cred && (
                <div className={styles.row}>
                  <span className={styles.label}>{spec.secretLabel}</span>
                  <span className={styles.mono}>{cred.secret_masked}</span>
                </div>
              )}

              {setting && Object.keys(setting.config).length > 0 && (
                <div className={styles.cfgGrid}>
                  {Object.entries(setting.config).map(([k, v]) => (
                    <div key={k} className={styles.cfgRow}>
                      <span className={styles.cfgKey}>{k}</span>
                      <span className={styles.cfgValue}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.actions}>
                <Button size="sm" variant="secondary" onClick={() => openEdit(spec)}>
                  Configurar
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {editing && (
        <div className={styles.modalBackdrop} onClick={closeEdit}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
          >
            <h2 className={styles.modalTitle}>{editing.label}</h2>
            <p className={styles.modalSub}>{editing.description}</p>

            <label className={styles.field}>
              <span>
                {editing.secretLabel}
                {credDo(editing, configForm.praca) ? ' (deixar vazio mantém o atual)' : ''}
              </span>
              <input
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="new-password"
              />
            </label>

            {editing.configFields.map((field) => (
              <label key={field.key} className={styles.field}>
                <span>{field.label}</span>
                {field.type === 'select' ? (
                  <select
                    value={configForm[field.key] ?? ''}
                    onChange={(e) => {
                      // Campo da praça troca a CREDENCIAL inteira, não só o valor.
                      if (editing.porPraca && field.key === 'praca') {
                        trocarPraca(editing, e.target.value);
                        return;
                      }
                      setConfigForm({
                        ...configForm,
                        [field.key]: e.target.value,
                      });
                    }}
                  >
                    <option value="">— não definido —</option>
                    {(field.optionsFrom
                      ? opcoesDinamicas[field.optionsFrom]
                      : (field.options ?? [])
                    ).map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type ?? 'text'}
                    value={configForm[field.key] ?? ''}
                    onChange={(e) =>
                      setConfigForm({
                        ...configForm,
                        [field.key]: e.target.value,
                      })
                    }
                    placeholder={field.placeholder}
                  />
                )}
                {configForm[field.key]?.startsWith(PREFIXO_MASCARA) && (
                  <span className={styles.hint}>
                    Valor guardado. Deixe como está para manter; digite por cima para trocar.
                  </span>
                )}
                {field.hint && (
                  <span className={styles.hint}>{field.hint}</span>
                )}
              </label>
            ))}

            {credDo(editing, configForm.praca) && (
              <label className={styles.check}>
                <input
                  type="checkbox"
                  checked={ativa}
                  onChange={(e) => setAtiva(e.target.checked)}
                />
                <span>Integração ativa — a esteira só usa credencial ativa.</span>
              </label>
            )}

            <div className={styles.modalActions}>
              <Button
                type="button"
                variant="ghost"
                onClick={() => void testar()}
                loading={probe.isPending}
              >
                Testar
              </Button>
              <Button type="button" variant="ghost" onClick={closeEdit}>
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={
                  upsertSetting.isPending ||
                  createCred.isPending ||
                  updateCred.isPending ||
                  rotateCred.isPending
                }
              >
                Guardar
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
